import React from 'react'
import {Card, Row, Col, Badge} from 'react-bootstrap'

export enum AccountBookTypes {
  GENERAL = 'general', // รายวันทั่วไป (JV)
  PURCHASE = 'purchase', // รายวันซื้อ (RR)
  SALE = 'sale', // รายวันขาย (IV)
  PAYMENT = 'payment', // รายวันจ่าย (PV)
  RECEIVED = 'received', // รายวันรับ (RV)

  PETTY_CASH_PAYMENT = 'petty_cash_payment', // รายวันจ่าย - เงินสดย่อย (PVC)
  ONLINE_PAYMENT = 'online_payment', // รายวันจ่าย - ขายสินค้าออนไลน์ (PVO)
  CHECK_PAYMENT = 'check_payment', // รายวันจ่ายเช็ค (QO)
  EXPENSE = 'expense', // รายวันค่าใช้จ่าย (AP)
  RETURN_RECEIVED = 'return_received', // รายวันรับคืนสินค้า (CN)
  RETURN_SENT = 'return_sent', // รายวันส่งคืนสินค้า (DN)
}

export enum AccountBookStates {
  PENDING = 'pending',
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  SHOULD_CONSIDER = 'should_consider',
  APPROVED = 'approved',
  TRASH = 'trash',
}

export interface GetAccountBook {
  id: number
  bookType: AccountBookTypes
  date: string
  code: string
  labCompanyId: number | null
  projectId: number | null
  sellerId: number | null
  departmentId: number | null
  branchId: number | null
  taxExpenseId: number | null
  accountDesc: string
  attachment: any
  refDoc: string
  saleChannel: string | null
  state: AccountBookStates
  totalDebit: number
  totalCredit: number
  createdAt: string
  updatedAt: string
}

interface ITabDocumentAccountBook {
  accountBook: GetAccountBook | null
}

const TabDocumentAccountBook: React.FC<ITabDocumentAccountBook> = ({accountBook}) => {
  const numberFormat = (num: number) => {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  const getBookTypeLabel = (bookType: AccountBookTypes) => {
    const types: Record<AccountBookTypes, {label: string; abbr: string}> = {
      [AccountBookTypes.GENERAL]: {label: 'รายวันทั่วไป', abbr: 'JV'},
      [AccountBookTypes.PURCHASE]: {label: 'รายวันซื้อ', abbr: 'RR'},
      [AccountBookTypes.SALE]: {label: 'รายวันขาย', abbr: 'IV'},
      [AccountBookTypes.PAYMENT]: {label: 'รายวันจ่าย', abbr: 'PV'},
      [AccountBookTypes.RECEIVED]: {label: 'รายวันรับ', abbr: 'RV'},
      [AccountBookTypes.PETTY_CASH_PAYMENT]: {label: 'รายวันจ่าย - เงินสดย่อย', abbr: 'PVC'},
      [AccountBookTypes.ONLINE_PAYMENT]: {label: 'รายวันจ่าย - ขายสินค้าออนไลน์', abbr: 'PVO'},
      [AccountBookTypes.CHECK_PAYMENT]: {label: 'รายวันจ่ายเช็ค', abbr: 'QO'},
      [AccountBookTypes.EXPENSE]: {label: 'รายวันค่าใช้จ่าย', abbr: 'AP'},
      [AccountBookTypes.RETURN_RECEIVED]: {label: 'รายวันรับคืนสินค้า', abbr: 'CN'},
      [AccountBookTypes.RETURN_SENT]: {label: 'รายวันส่งคืนสินค้า', abbr: 'DN'},
    }
    return types[bookType]
  }

  const getStateLabel = (state: AccountBookStates) => {
    const states: Record<AccountBookStates, string> = {
      [AccountBookStates.DRAFT]: 'แบบร่าง',
      [AccountBookStates.PENDING]: 'รอดำเนินการ',
      [AccountBookStates.PENDING_APPROVAL]: 'รออนุมัติ',
      [AccountBookStates.SHOULD_CONSIDER]: 'ควรพิจารณา',
      [AccountBookStates.APPROVED]: 'อนุมัติแล้ว',
      [AccountBookStates.TRASH]: 'ถังขยะ',
    }
    return states[state]
  }

  const getStateBadgeVariant = (state: AccountBookStates) => {
    const variants: Record<AccountBookStates, string> = {
      [AccountBookStates.DRAFT]: 'secondary',
      [AccountBookStates.PENDING]: 'warning',
      [AccountBookStates.PENDING_APPROVAL]: 'info',
      [AccountBookStates.SHOULD_CONSIDER]: 'warning',
      [AccountBookStates.APPROVED]: 'success',
      [AccountBookStates.TRASH]: 'danger',
    }
    return variants[state]
  }

  const getBookTypeBadgeVariant = (bookType: AccountBookTypes) => {
    const variants: Record<AccountBookTypes, string> = {
      [AccountBookTypes.GENERAL]: 'secondary',
      [AccountBookTypes.PURCHASE]: 'danger',
      [AccountBookTypes.SALE]: 'primary',
      [AccountBookTypes.PAYMENT]: 'danger',
      [AccountBookTypes.RECEIVED]: 'success',
      [AccountBookTypes.PETTY_CASH_PAYMENT]: 'warning',
      [AccountBookTypes.ONLINE_PAYMENT]: 'info',
      [AccountBookTypes.CHECK_PAYMENT]: 'dark',
      [AccountBookTypes.EXPENSE]: 'warning',
      [AccountBookTypes.RETURN_RECEIVED]: 'info',
      [AccountBookTypes.RETURN_SENT]: 'dark',
    }
    return variants[bookType]
  }

  if (!accountBook) {
    return (
      <Card>
        <Card.Body className='text-center py-5'>
          <div className='mb-3'>
            <i className='bi bi-file-earmark-text fs-1 text-muted'></i>
          </div>
          <p className='text-muted fs-5'>ไม่พบข้อมูลสมุดบัญชี</p>
        </Card.Body>
      </Card>
    )
  }

  const balance = accountBook.totalDebit - accountBook.totalCredit
  const bookTypeInfo = getBookTypeLabel(accountBook.bookType)

  return (
    <div>
      {/* Header Card */}
      <Card className='mb-3'>
        <Card.Body>
          <Row className='mb-4'>
            <Col>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <h4 className='mb-0'>ข้อมูลสมุดบัญชี</h4>
                <Badge
                  bg={getBookTypeBadgeVariant(accountBook.bookType)}
                  className='fs-6 text-light'
                >
                  {bookTypeInfo.abbr}
                </Badge>
              </div>
              <p className='text-muted mb-0'>{bookTypeInfo.label}</p>
            </Col>
            <Col xs='auto'>
              <Badge bg={getStateBadgeVariant(accountBook.state)} className='fs-6 px-3 py-2'>
                {getStateLabel(accountBook.state)}
              </Badge>
            </Col>
          </Row>

          <Row className='g-4 mb-3'>
            <Col md={6}>
              <div>
                <small className='text-muted'>เลขที่เอกสาร</small>
                <a
                  href={`/accountant/dailyJournalDetail/${accountBook.id}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <p className='fs-5 fw-bold mb-0 text-info' style={{cursor: 'pointer'}}>
                    #{accountBook.code}
                  </p>
                </a>
              </div>
            </Col>
            <Col md={6}>
              <div>
                <small className='text-muted'>วันที่บันทึก</small>
                <p className='fs-5 fw-bold mb-0'>{formatDate(accountBook.date)}</p>
              </div>
            </Col>
            <Col md={6}>
              <div>
                <small className='text-muted'>เอกสารอ้างอิง</small>
                <p className='fs-5 fw-bold mb-0'>{accountBook.refDoc || '-'}</p>
              </div>
            </Col>
            <Col md={6}>
              <div>
                <small className='text-muted'>ช่องทางการขาย</small>
                <p className='fs-5 fw-bold mb-0'>{accountBook.saleChannel || '-'}</p>
              </div>
            </Col>
          </Row>

          {accountBook.accountDesc && (
            <div className='bg-light rounded p-3 mt-3'>
              <small className='text-muted d-block mb-1'>รายละเอียด</small>
              <p className='mb-0'>{accountBook.accountDesc}</p>
            </div>
          )}
        </Card.Body>

        {/* Summary Section */}
        <Card.Body className='bg-light border-top'>
          <h5 className='mb-3'>สรุปยอดเงิน</h5>
          <Row className='g-3'>
            <Col md={4}>
              <Card className='border-start border-primary border-4'>
                <Card.Body>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <small className='text-muted fw-medium'>ยอดเดบิต</small>
                    <Badge bg='primary' className='fs-6 text-light'>
                      DEBIT
                    </Badge>
                  </div>
                  <h3 className='text-primary mb-0'>{numberFormat(accountBook.totalDebit)}</h3>
                  <small className='text-muted'>บาท</small>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className='border-start border-warning border-4'>
                <Card.Body>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <small className='text-muted fw-medium'>ยอดเครดิต</small>
                    <Badge bg='warning' className='fs-6'>
                      CREDIT
                    </Badge>
                  </div>
                  <h3 className='text-warning mb-0'>{numberFormat(accountBook.totalCredit)}</h3>
                  <small className='text-muted'>บาท</small>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card
                className={`border-start ${
                  balance >= 0 ? 'border-success' : 'border-danger'
                } border-4`}
              >
                <Card.Body>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <small className='text-muted fw-medium'>ยอดคงเหลือ</small>
                    <Badge bg={balance >= 0 ? 'success' : 'danger'} className='fs-6'>
                      BALANCE
                    </Badge>
                  </div>
                  <h3 className={balance >= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                    {numberFormat(Math.abs(balance))}
                  </h3>
                  <small className='text-muted'>
                    บาท {balance >= 0 ? '(เดบิต มากกว่า)' : '(เครดิต มากกว่า)'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>

        {/* Additional Information */}
        <Card.Body className='border-top'>
          <h5 className='mb-3'>ข้อมูลเพิ่มเติม</h5>
          <Row className='g-3'>
            {/* <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสสมุดบัญชี</small>
                <p className='fw-semibold mb-0'>{accountBook.id}</p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสบริษัท</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.labCompanyId !== null ? accountBook.labCompanyId : '-'}
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสสาขา</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.branchId !== null ? accountBook.branchId : '-'}
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสโครงการ</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.projectId !== null ? accountBook.projectId : '-'}
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสแผนก</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.departmentId !== null ? accountBook.departmentId : '-'}
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสผู้ขาย</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.sellerId !== null ? accountBook.sellerId : '-'}
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>รหัสค่าใช้จ่ายภาษี</small>
                <p className='fw-semibold mb-0'>
                  {accountBook.taxExpenseId !== null ? accountBook.taxExpenseId : '-'}
                </p>
              </div>
            </Col> */}

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>วันที่สร้าง</small>
                <p className='fw-semibold mb-0'>{formatDate(accountBook.createdAt)}</p>
              </div>
            </Col>

            <Col md={4}>
              <div className='bg-light rounded p-3 h-100'>
                <small className='text-muted d-block mb-1'>วันที่แก้ไขล่าสุด</small>
                <p className='fw-semibold mb-0'>{formatDate(accountBook.updatedAt)}</p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  )
}

export default TabDocumentAccountBook
